import { useEffect } from "react";
import KeyboardShortcutHint from "../KeyboardShortcutHint";
import Icon from "../Icon";
import { type Platform } from "../../hooks/usePlatformDetection";

export interface SearchButtonProps {
  onClick?: () => void;
  disabled?: boolean;
  testId?: string;
  className?: string;
  /** Force display of specific platform keyboard shortcut (for testing/stories only) */
  forcePlatform?: Platform;
}

export default function SearchButton({
  onClick,
  disabled = false,
  testId = "search-button",
  className = "",
  forcePlatform
}: SearchButtonProps) {
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Check for Cmd+k (Mac) or Ctrl+k (Windows/Linux)
      if ((event.metaKey || event.ctrlKey) && event.key === "k") {
        event.preventDefault();
        if (!disabled && onClick) {
          onClick();
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [onClick, disabled]);

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      aria-label="search"
      className={`
        bg-extended-pgasilver-brand-color 
        box-border 
        flex 
        flex-row 
        w-[100px]
        lg:min-w-[160px]
        items-center 
        justify-start 
        overflow-hidden 
        p-0 
        relative 
        rounded-lg 
        hover:bg-opacity-80 
        focus:bg-opacity-80 
        active:bg-opacity-90 
        disabled:opacity-50 
        disabled:cursor-not-allowed 
        transition-all 
        duration-200 
        cursor-pointer
        ${className}
      `
        .trim()
        .replace(/\s+/g, " ")}
    >
      <div className="flex flex-row gap-2 grow h-full items-center justify-start pl-2 pr-4 py-2.5">
        <div className="flex items-center justify-center shrink-0 size-6">
          <Icon name="search" size={24} className="text-extended-pgasilver-brand-on-color" />
        </div>
        <div className="flex items-center text-title-small text-extended-pgasilver-brand-on-color text-left text-nowrap tracking-[0.024px] leading-[24px]">
          Find
        </div>
        <KeyboardShortcutHint
          keyLabel="k"
          className="ml-auto hidden lg:block bg-sys-brand-surface/90"
          forcePlatform={forcePlatform}
        />
      </div>
    </button>
  );
}
